/**
 * authRoutes.tsx — unauthenticated auth + onboarding routes
 * Extracted from App.tsx in #2470 (pure refactor, no behaviour changes).
 *
 * Covers: /login, /signup, /signup/verify, /forgot-password,
 *         /auth/callback, /callback, /auth/magic-link, /auth/redeem/:token
 */
import React, { lazy, Suspense } from "react";
import { Route } from "react-router-dom";

import { FleetLogin } from "../pages/FleetLogin";
import { FleetSignup } from "../pages/FleetSignup";
import { FleetForgotPassword } from "../pages/FleetForgotPassword";
import { FleetAuthCallback } from "../pages/FleetAuthCallback";
import type { PageFallbackProps } from "./types";

// Lazy-loaded auth pages
const FleetSignupVerify    = lazy(() => import("../pages/FleetSignupVerify").then(m => ({ default: m.FleetSignupVerify })));
// #2316 — OAuth callback (Google/GitHub via Cognito hosted UI)
const AuthCallbackPage     = lazy(() => import("../pages/AuthCallbackPage").then(m => ({ default: m.AuthCallbackPage })));
// #2318 — magic-link login
const MagicLinkRequestPage = lazy(() => import("../pages/MagicLinkRequestPage").then(m => ({ default: m.MagicLinkRequestPage })));
const MagicLinkRedeemPage  = lazy(() => import("../pages/MagicLinkRedeemPage").then(m => ({ default: m.MagicLinkRedeemPage })));

interface Props {
  PageFallback: React.ComponentType<PageFallbackProps>;
}

/** Returns the array of auth-flow <Route> elements. */
export function getAuthRoutes({ PageFallback }: Props): React.ReactElement[] {
  const F = <PageFallback />;

  return [
    <Route key="login"           path="/login"           element={<FleetLogin />} />,
    <Route key="signup"          path="/signup"          element={<FleetSignup />} />,
    <Route key="signup-verify"   path="/signup/verify"   element={<Suspense fallback={F}><FleetSignupVerify /></Suspense>} />,
    <Route key="forgot-password" path="/forgot-password" element={<FleetForgotPassword />} />,
    // #2316 — OAuth (Google/GitHub) hosted-UI callback — NO auth wrapper
    <Route key="auth-callback"   path="/auth/callback"   element={<FleetAuthCallback />} />,
    // #2316 — Cognito hosted-UI redirect target
    <Route key="callback"        path="/callback"        element={<Suspense fallback={F}><AuthCallbackPage /></Suspense>} />,
    // #2318 — magic-link login
    <Route key="magic-link"      path="/auth/magic-link"    element={<Suspense fallback={F}><MagicLinkRequestPage /></Suspense>} />,
    <Route key="magic-redeem"    path="/auth/redeem/:token"  element={<Suspense fallback={F}><MagicLinkRedeemPage /></Suspense>} />,
  ];
}
